/**
 * Parwaaz — name normalisation, fuzzy matching and MRZ-style transliteration.
 * Used to compare a pilot's typed name against their MRZ / certificate / signature name
 * without being defeated by diacritics, punctuation, word order or minor typos.
 */

/** Strip combining diacritical marks left over after NFD normalisation. */
function stripDiacritics(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '');
}

/**
 * Uppercase, diacritic-stripped, punctuation-free, whitespace-collapsed, token-sorted form of
 * a name — used only for similarity comparison (not for display).
 */
export function normaliseName(s: string): string {
  const cleaned = stripDiacritics(s.toUpperCase())
    .replace(/[^A-Z0-9\s]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return cleaned.sort().join(' ');
}

/** Jaro-Winkler similarity in [0, 1]; 1 = identical. */
export function jaroWinkler(a: string, b: string): number {
  if (a === b) return 1;
  if (a.length === 0 || b.length === 0) return 0;

  const matchDistance = Math.max(Math.floor(Math.max(a.length, b.length) / 2) - 1, 0);
  const aMatches = new Array<boolean>(a.length).fill(false);
  const bMatches = new Array<boolean>(b.length).fill(false);

  let matches = 0;
  for (let i = 0; i < a.length; i++) {
    const start = Math.max(0, i - matchDistance);
    const end = Math.min(i + matchDistance + 1, b.length);
    for (let j = start; j < end; j++) {
      if (bMatches[j] || a[i] !== b[j]) continue;
      aMatches[i] = true;
      bMatches[j] = true;
      matches++;
      break;
    }
  }
  if (matches === 0) return 0;

  let transpositions = 0;
  let k = 0;
  for (let i = 0; i < a.length; i++) {
    if (!aMatches[i]) continue;
    while (!bMatches[k]) k++;
    if (a[i] !== b[k]) transpositions++;
    k++;
  }
  transpositions = transpositions / 2;

  const jaro = (matches / a.length + matches / b.length + (matches - transpositions) / matches) / 3;

  // Winkler boost: up to 4 leading matching characters, scaling factor 0.1.
  let prefix = 0;
  while (prefix < 4 && prefix < a.length && prefix < b.length && a[prefix] === b[prefix]) prefix++;
  return jaro + prefix * 0.1 * (1 - jaro);
}

/** Jaro-Winkler similarity of two names after normalisation. */
export function nameSimilarity(a: string, b: string): number {
  return jaroWinkler(normaliseName(a), normaliseName(b));
}

// Multi-character folds applied before generic diacritic stripping — these do not survive
// NFD decomposition on their own (e.g. Ä decomposes to A + combining diaeresis, which would
// just become "A" anyway, but ß, Æ, Œ have no single-letter Unicode decomposition).
const FOLD_MAP: [RegExp, string][] = [
  [/ß/g, 'SS'],
  [/Æ/g, 'AE'],
  [/Œ/g, 'OE'],
  [/Ø/g, 'O'],
];

/** Fold one name part to MRZ-safe A-Z/space form. */
function foldPart(s: string): string {
  let out = s.toUpperCase();
  for (const [re, rep] of FOLD_MAP) out = out.replace(re, rep);
  out = stripDiacritics(out);
  out = out.replace(/[^A-Z\s]/g, ' ').replace(/\s+/g, ' ').trim();
  return out;
}

/**
 * MRZ-style transliteration for the printed card: A-Z and space only, diacritics folded to
 * their base letter (Ä→A, not AE), formatted `SURNAME, GIVEN`, truncated to 30 characters.
 */
export function translitForCard(surname: string, givenNames: string): string {
  const formatted = `${foldPart(surname)}, ${foldPart(givenNames)}`;
  return formatted.slice(0, 30);
}
